// Interval helpers for the Shore-S incremental image cache.
//
// Ranges are represented as [startISO, endISO] tuples. `coalesce` merges
// overlapping/adjacent ranges; `subtractIntervals` returns the portions of a
// target range that are NOT yet covered (the "gaps" we still need to fetch).

const toMs = (d) => new Date(d).getTime();
const toISO = (ms) => new Date(ms).toISOString();

/**
 * @function coalesce
 * @description Merge overlapping/adjacent ranges into a minimal sorted set.
 * @param {Array<Array<string>>} ranges - Each a `[start, end]` pair.
 * **Called by:
 * @see {mergeRangeResult}
 * @see {subtractIntervals}
 * @memberof module:ShoreS
 * @returns {Array<Array<string>>} Each a `[start, end]` pair.
 */
export const coalesce = (ranges) => {
  if (!ranges || ranges.length === 0) return [];

  const sorted = ranges.map((r) => [toMs(r[0]), toMs(r[1])]).sort((a, b) => a[0] - b[0]);

  const merged = [sorted[0].slice()];
  for (let i = 1; i < sorted.length; i += 1) {
    const last = merged[merged.length - 1];
    if (sorted[i][0] <= last[1]) {
      last[1] = Math.max(last[1], sorted[i][1]);
    } else {
      merged.push(sorted[i].slice());
    }
  }

  return merged.map(([s, e]) => [toISO(s), toISO(e)]);
};

/**
 * @function subtractIntervals
 * @description Subtract the already-covered ranges from the target range, returning the
 * uncovered gaps that still need to be fetched. Empty result => nothing to do.
 * @param {Array<string>} target - Requested range, as a `[start, end]` pair.
 * @param {Array<Array<string>>} covered - Already-cached ranges, each a `[start, end]` pair.
 * **Called by:
 * @see {shoreS/saga}
 * @memberof module:ShoreS
 * @returns {Array<Array<string>>} Each a `[start, end]` pair.
 */
export const subtractIntervals = (target, covered) => {
  let gaps = [[toMs(target[0]), toMs(target[1])]];
  if (gaps[0][1] <= gaps[0][0]) return [];

  const cov = coalesce(covered).map((r) => [toMs(r[0]), toMs(r[1])]);

  cov.forEach(([cs, ce]) => {
    const next = [];
    gaps.forEach(([gs, ge]) => {
      // no overlap
      if (ce <= gs || cs >= ge) {
        next.push([gs, ge]);
        return;
      }
      // left remainder
      if (cs > gs) next.push([gs, Math.min(cs, ge)]);
      // right remainder
      if (ce < ge) next.push([Math.max(ce, gs), ge]);
    });
    gaps = next;
  });

  return gaps.filter(([s, e]) => e > s).map(([s, e]) => [toISO(s), toISO(e)]);
};
